import React, { useCallback, useMemo } from 'react';
import { Popover, Box, Select, SelectOptionProps, Divider } from '@televet/kibble-ui';
import { WorkflowEvent, WorkflowEventTriggerType } from 'shared/types/graphql';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { updateAutomationErrors, updateAutomationDraft, updateAutomationType } from '../../../state/automationsSlice';
import { AutomationsFieldLabel } from '../../Layout';
import { useGetParentAutomations, useIsChildAutomation } from 'pages/Automations/hooks';
import { ExtendedWorkflowEvent } from 'pages/Automations/types/AutomationDraft';
import { useGetFieldErrorText } from 'pages/Automations/hooks/useGetFieldErrorText';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums';
import { useCurrentAutomationId } from 'pages/Automations/hooks/useCurrentAutomationId';

const automationTypes: SelectOptionProps[] = [
  { label: 'From Another Automation', value: ExtendedWorkflowEvent.FromAnotherAutomation },
  { label: 'Appointment Follow Up', value: WorkflowEvent.AppointmentFollowup },
  {
    label: 'Appointment Client Arrival',
    value: WorkflowEvent.AppointmentClientArrival,
  },
  {
    label: 'Appointment Confirm Reminder',
    value: WorkflowEvent.AppointmentConfirmReminder,
  },
  {
    label: 'Appointment Reminder',
    value: WorkflowEvent.AppointmentReminder,
  },
  {
    label: 'Appointment Scheduled',
    value: WorkflowEvent.AppointmentScheduled,
  },
  {
    label: 'Appointment PIMS Sync',
    value: WorkflowEvent.AppointmentPimsSync,
  },
  {
    label: 'Website Widget Request',
    value: WorkflowEvent.WidgetRequestFormSubmitted,
  },
  {
    label: 'Lapsed Client',
    value: WorkflowEvent.LapsedPetParent,
  },
];

export const AutomationType = (): JSX.Element => {
  const { isFeatureEnabled } = useFeatureFlag();
  const currentAutomationId = useCurrentAutomationId();
  const automationType = useAppSelector((state) => state.automations.automationDraft.automationType);
  const triggerType = useAppSelector((state) => state.automations.automationDraft.triggerType);
  const isChildAutomation = useIsChildAutomation({ automationType, triggerType });
  const { parentAutomations } = useGetParentAutomations(currentAutomationId);
  const fieldErrors = useAppSelector((state) => state.automations.errors.automationType);
  const errorText = useGetFieldErrorText(fieldErrors);
  const dispatch = useAppDispatch();
  const isGlobalEditor = useAppSelector((state) => state.automations.isGlobalEditor);

  const handleAutomationTypeChange = useCallback(
    (selectedOption: SelectOptionProps) => {
      if (selectedOption.value === ExtendedWorkflowEvent.FromAnotherAutomation) {
        dispatch(
          updateAutomationDraft({
            triggerType: WorkflowEventTriggerType.Manual,
            automationType: WorkflowEvent.AppointmentReminder,
          }),
        );
      } else {
        dispatch(updateAutomationType(selectedOption.value as WorkflowEvent));
      }

      if (Boolean(fieldErrors.length)) {
        dispatch(updateAutomationErrors({ automationType: [] }));
      }
    },
    [dispatch, fieldErrors],
  );

  const convertedAutomationType = useMemo(() => {
    if (!automationType) return '';
    if (isChildAutomation) {
      return ExtendedWorkflowEvent.FromAnotherAutomation;
    } else {
      return automationType;
    }
  }, [isChildAutomation, automationType]);

  const isDisabled = useMemo(() => {
    return isChildAutomation && parentAutomations?.length > 0;
  }, [isChildAutomation, parentAutomations]);

  return (
    <>
      <AutomationsFieldLabel>Automation Type</AutomationsFieldLabel>
      <Box mt={0} mb={10} pos="relative">
        {isDisabled && (
          <Popover
            size="lg"
            placement="top"
            trigger="hover"
            headerProps={{
              children: 'Linked Automations need to be removed before automation type can be changed.',
            }}
            anchor={<Box zIndex={10} display="block" width="100%" height="100%" pos="absolute" />}
          >
            Navigate to linked automations by clicking the tags below, then remove this automation from ‘Next Step’
            fields within the prompts.
          </Popover>
        )}
        <Select
          listProps={{
            onSelect: handleAutomationTypeChange,
          }}
          isInvalid={Boolean(fieldErrors.length)}
          errorText={errorText}
          value={convertedAutomationType}
          options={[
            ...automationTypes.filter(
              (automationType) => !isGlobalEditor || automationType.value !== WorkflowEvent.WidgetRequestFormSubmitted,
            ),
            ...(isGlobalEditor
              ? [
                  {
                    label: 'Invoice Line Item',
                    value: WorkflowEvent.InvoiceLineItemCreated,
                  },
                ]
              : []),
            ...(isFeatureEnabled(FeatureFlagName.CallDeflection)
              ? [
                  {
                    label: 'Call Concierge Form Submitted',
                    value: WorkflowEvent.CallDeflectionFormSubmitted,
                  },
                ]
              : []),
          ]}
          placeholder="Select Type"
          mt={2}
          isDisabled={isDisabled}
          className="Automations__Select"
        />
      </Box>
      {automationType && <Divider />}
    </>
  );
};
